/** Сравнение отклонений — порт renderDeviationComparisonV3 (build-скрипт, стр. 4336-4400):
 * карточки по отмеченным «Сравнить» артикулам, метрики к средним по выбранным + флаги. */
import React, { useMemo } from "react";

import { cn } from "@/lib/utils";
import { fmtDate, int0, signed } from "./format";
import { rowMetrics, type RowMetrics, type ViewContext, type ViewRow } from "./metrics";

type FlagLevel = "risk" | "warn" | "good";

interface Flag {
  level: FlagLevel;
  text: string;
}

const CARD_CLS: Record<FlagLevel, string> = {
  risk: "border-red-300 dark:border-red-900",
  warn: "border-amber-300 dark:border-amber-900",
  good: "border-emerald-300 dark:border-emerald-900",
};

const FLAG_CLS: Record<FlagLevel, string> = {
  risk: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  warn: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  good: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
};

function pct(value: number, base: number): number {
  return base ? Math.round(((value - base) / base) * 100) : 0;
}

function pctText(v: number): string {
  return `${v > 0 ? "+" : ""}${v}%`;
}

export function ComparisonPanel({
  rows,
  ctx,
  compared,
}: {
  rows: ViewRow[];
  ctx: ViewContext;
  compared: Set<string>;
}): React.ReactElement | null {
  const selected = useMemo(
    () =>
      rows
        .filter((r) => compared.has(r.key))
        .map((row) => ({ row, m: rowMetrics(row, ctx) })),
    [rows, ctx, compared],
  );

  if (!selected.length) return null;

  const avgLast30 = selected.reduce((s, x) => s + x.m.last30, 0) / selected.length;
  const avgStock = selected.reduce((s, x) => s + x.row.stockTotal, 0) / selected.length;
  const withDays = selected.filter((x) => x.m.stockDays !== null);
  const avgStockDays = withDays.length
    ? withDays.reduce((s, x) => s + (x.m.stockDays ?? 0), 0) / withDays.length
    : null;

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="text-sm font-medium">Сравнение отклонений · выбрано {selected.length}</div>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {selected.map(({ row, m }) => {
          const flags = computeFlags(row, m, avgLast30, avgStock, avgStockDays);
          const level: FlagLevel = flags.some((f) => f.level === "risk")
            ? "risk"
            : flags.some((f) => f.level === "warn")
              ? "warn"
              : "good";
          const daysDev =
            m.stockDays !== null && avgStockDays !== null
              ? Math.round(m.stockDays - avgStockDays)
              : null;
          return (
            <div key={row.key} className={cn("rounded-md border bg-card p-2.5 text-xs", CARD_CLS[level])}>
              <div className="truncate font-medium" title={`${row.article} · ${row.name}`}>
                {row.article} · {row.name}
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
                <Metric label="Δ MTD" value={`${signed(m.deltaMtd)} / ${pctText(pct(m.currentMtd, m.previousMtd || 1))}`} />
                <Metric label="30 дней к выбранным" value={pctText(pct(m.last30, avgLast30))} />
                <Metric label="Остаток к выбранным" value={pctText(pct(row.stockTotal, avgStock))} />
                <Metric
                  label="Запас к выбранным"
                  value={daysDev === null ? "нет базы" : `${signed(daysDev)} дн.`}
                />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {flags.map((f, i) => (
                  <span key={i} className={cn("rounded px-1.5 py-0.5 text-[10px]", FLAG_CLS[f.level])}>
                    {f.text}
                  </span>
                ))}
              </div>
              <div className="mt-1.5 text-[10px] text-muted-foreground">
                Факт: MTD {int0(m.currentMtd)} против {int0(m.previousMtd)} · 30 дней {int0(m.last30)} ·
                остаток {int0(row.stockTotal)} · посл. продажа {fmtDate(row.lastSale)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}

/** Порт таблицы флагов (стр. 4372-4390). */
function computeFlags(
  row: ViewRow,
  m: RowMetrics,
  avgLast30: number,
  avgStock: number,
  avgStockDays: number | null,
): Flag[] {
  const flags: Flag[] = [];
  if (m.deltaMtd < 0) {
    flags.push({ level: "risk", text: `MTD ниже прошлого периода на ${Math.abs(pct(m.currentMtd, m.previousMtd || 1))}%` });
  } else if (m.deltaMtd > 0) {
    flags.push({ level: "good", text: "MTD выше прошлого периода" });
  }
  if (avgLast30 > 0 && m.last30 < avgLast30 * 0.75) {
    flags.push({ level: "warn", text: `спрос за 30 дней ниже выбранных на ${Math.abs(pct(m.last30, avgLast30))}%` });
  } else if (avgLast30 > 0 && m.last30 > avgLast30 * 1.25) {
    flags.push({ level: "good", text: "спрос за 30 дней выше выбранных" });
  }
  if (m.stockDays !== null && m.stockDays <= 30) {
    flags.push({
      level: m.stockDays <= 14 ? "risk" : "warn",
      text: `запас до 30 дней (${m.stockDays} дн.)`,
    });
  }
  if (m.stockDays !== null && avgStockDays !== null && m.stockDays - avgStockDays < 0) {
    flags.push({
      level: "warn",
      text: `запас ниже среднего выбранных на ${Math.abs(Math.round(m.stockDays - avgStockDays))} дн.`,
    });
  }
  if (row.stockTotal > avgStock * 1.4 && m.last30 < avgLast30) {
    flags.push({ level: "warn", text: "остаток выше среднего, спрос ниже" });
  }
  if (row.stockMp <= 0 && m.last30 > 0) {
    flags.push({ level: "risk", text: "есть продажи без остатка МП" });
  }
  if (!flags.length) {
    flags.push({ level: "good", text: "критичных отклонений не видно" });
  }
  return flags;
}
